import { ConfigurationGroupOptionType, ConfigurationGroupType } from 'shared-types/SpaPackage';

export type OptionRef = {
  configurationGroupId: number;
  optionId: number;
};

type GuestSelection = OptionRef & {
  isSelected: boolean;
  timeslot?: { startTime: string } | null;
};

type GuestConfigurationGroups =
  | Record<string, (GuestSelection | null | undefined)[] | undefined>
  | undefined;

export const TREATMENT_OPTION_TYPE = 'spa-treatments';

export function isTreatmentOption(option: Pick<ConfigurationGroupOptionType, 'type'>): boolean {
  return option.type === TREATMENT_OPTION_TYPE;
}

const optionKey = ({ configurationGroupId, optionId }: OptionRef) =>
  `${configurationGroupId}:${optionId}`;

export function isTreatmentRef(treatmentOptions: Set<string>, option: OptionRef): boolean {
  return treatmentOptions.has(optionKey(option));
}

export function getTreatmentOptionRefs(groups: ConfigurationGroupType[]): Set<string> {
  const refs = new Set<string>();

  groups.forEach((group) => {
    group.options.forEach((option) => {
      if (isTreatmentOption(option)) {
        refs.add(optionKey({ configurationGroupId: group.id, optionId: option.id }));
      }
    });
  });

  return refs;
}

export function getGuestBookedStartTimes(
  groups: GuestConfigurationGroups,
  treatmentOptions: Set<string>
): Map<string, OptionRef[]> {
  const booked = new Map<string, OptionRef[]>();

  Object.values(groups ?? {}).forEach((selections) => {
    (selections ?? []).forEach((selection) => {
      if (!selection?.isSelected || !selection.timeslot) return;
      if (!isTreatmentRef(treatmentOptions, selection)) return;

      const holders = booked.get(selection.timeslot.startTime) ?? [];

      holders.push({
        configurationGroupId: selection.configurationGroupId,
        optionId: selection.optionId,
      });

      booked.set(selection.timeslot.startTime, holders);
    });
  });

  return booked;
}

export function isStartTimeTakenByOtherOption(
  booked: Map<string, OptionRef[]>,
  startTime: string,
  option: OptionRef
): boolean {
  return (booked.get(startTime) ?? []).some(
    (holder) =>
      holder.configurationGroupId !== option.configurationGroupId ||
      holder.optionId !== option.optionId
  );
}

export function withoutGroupHolders(
  booked: Map<string, OptionRef[]>,
  configurationGroupId: number
): Map<string, OptionRef[]> {
  const remaining = new Map<string, OptionRef[]>();

  booked.forEach((holders, startTime) => {
    const others = holders.filter((holder) => holder.configurationGroupId !== configurationGroupId);
    if (others.length > 0) remaining.set(startTime, others);
  });

  return remaining;
}
